import { resetLayout, revealPanel, togglePanel } from '~/comps/Dock/api'
import { captureImage } from '~/lib/capture'
import { clearAppCache } from '~/lib/clearAppCache'
import tempo, { MAX_BPM, MIN_BPM } from '~/lib/clock/tempo'
import { IS_DOCK, IS_MOBILE, SESSION_STATUS } from '~/lib/constants'
import { flashPanel } from '~/lib/flashPanel'
import { openMirror } from '~/lib/mirrorWindow'
import { navigateTo } from '~/lib/navigate'
import socket from '~/lib/socket'
import store from '~/store'
import { selectSessionStatus } from '~/store/api/selectors'
import { selectIsConnected } from '~/store/app/selectors'
import { toggleShowStats } from '~/store/app'
import {
	addProject,
	copyAction,
	cutAction,
	deleteAction,
	downloadAsset,
	downloadImage,
	downloadRecording,
	downloadRecordingSource,
	openExamplesProject,
	openRecording,
	pasteAction,
	saveAsTemplate,
	setAsImageStyle,
	setPresetAsProjectThumb,
	shareRecording,
	startRename,
	startSession,
	toggleOutput,
} from '~/store/app/thunks'
import { fillSourcesOf } from '~/lib/sourceFill'
import { toggleLearn } from '~/store/input/thunks'
import { isFileSource } from '~/lib/sources/ids'
import { armPresetSources } from '~/lib/sources/arming'
import { pickSourceItem } from '~/lib/sourcePresets'
import * as playhead from '~/lib/sources/playhead'
import { setCurrentTool } from '~/store/editor'
import { selectSelectedObjectIds } from '~/store/editor/selectors'
import { closeMap, detachFromMap, openActiveMap } from '~/store/live/mapThunks'
import {
	breakApartTextThunk,
	bringSelectedObjectsToFrontThunk,
	expandTextThunk,
	flipSelectedObjectsThunk,
	groupSelectedObjectsThunk,
	lockSelectedObjectsThunk,
	resetObjectsThunk,
	sendSelectedObjectsToBackThunk,
	subtractSelectedObjectsThunk,
	unGroupSelectedObjectsThunk,
	uniteSelectedObjectsThunk,
	unlockSelectedObjectsThunk,
} from '~/store/live/thunks'
import { selectProjectionMap } from '~/store/outputMapping'
import {
	addOrSavePreset,
	addPreset,
	hideExamples,
	loadNextPreset,
	loadPrevPreset,
	recaptureThumbnail,
	savePreset,
	toggleExamplesInPlaylist,
	togglePlayback,
} from '~/store/playback/thunks'
import { requestConfirm, setPanelCollapsed, setUIProperty, toggleUIProperty } from '~/store/ui'
import { APP_URL, AUTH_CLIENTID, AUTH_DOMAIN, DOWNLOAD_DAEMON_URL, IS_DEBUG, IS_DEV, IS_LOCAL } from './constants'

/** Whether a file fill is on stage — the discriminator every video action shares. */
const hasVideoFile = () =>
	[...fillSourcesOf(store.getState().live.objects)].some(isFileSource) && !!window.video_file_element

// Under the dock the panel's open state lives in the dock, not in `ui.show`. `triggerAction` dispatches what a function action returns, so the dock branch returns a thunk.
const toggleDockPanel = id => (IS_DOCK ? () => () => togglePanel(id) : toggleUIProperty(`show.${id}`))

function expandAndFlashPanel(panelId, focusSelector) {
	if (IS_DOCK) return () => revealPanel(panelId, focusSelector)
	return dispatch => {
		const stack = document.getElementById('right-panels')
		if (stack) stack.dataset.noAnimate = ''
		dispatch(setUIProperty([`show.${panelId}`, true]))
		dispatch(setPanelCollapsed({ id: panelId, collapsed: false }))
		requestAnimationFrame(() => {
			if (stack) delete stack.dataset.noAnimate
			flashPanel(panelId)
			if (focusSelector) {
				document.querySelector(focusSelector)?.focus()
			}
		})
	}
}

/**
 * The single logout procedure — every logout path must call this.
 *
 * Self-contained: no hooks, no auth0-react SDK, no global state — so it works in any auth state (authenticated, error page, pre-auth) from React or non-React code.
 *
 * 1. Clears auth0-spa-js cached tokens from localStorage (the `@@auth0spajs@@`
 *    prefix is an internal SDK detail, stable across the v2 line — revisit on a major SDK bump). Mirrors the local-session clear the SDK's logout() does.
 * 2. Redirects to Auth0 /v2/logout, which clears the SSO session cookie, with
 *    returnTo = the PUBLIC /login route. That route is outside AuthGuard, so nothing auto-fires loginWithRedirect and pre-empts the logout (the bug this replaced). See context/auth.md → Logout Flow.
 */
export const logout = () => {
	Object.keys(localStorage)
		.filter(k => k.startsWith('@@auth0spajs@@'))
		.forEach(k => localStorage.removeItem(k))

	const url = new URL(`https://${AUTH_DOMAIN}/v2/logout`)
	url.searchParams.set('client_id', AUTH_CLIENTID)
	url.searchParams.set('returnTo', `${APP_URL}/login`)
	window.location.assign(url.toString())
}

export const actions = {
	// project
	new_project: {
		label: 'New',
		action: () => addProject(),
		category: 'Project',
	},
	open_project: {
		label: 'Open',
		action: () => expandAndFlashPanel('projects'),
		category: 'Project',
	},
	save_project: {
		label: 'Save',
		action: () => addOrSavePreset(),
		category: 'Project',
	},
	save_as_template: {
		label: 'Save as Template',
		action: () => saveAsTemplate(),
		category: 'Project',
	},
	save_template_as: {
		label: 'Save template as project',
		callback: () => navigateTo('/save-template-as'),
		category: 'Project',
	},
	arm_preset_sources: {
		label: 'Keep sources used by presets warm',
		condition: () => !IS_MOBILE,
		callback: () => armPresetSources(),
		category: 'Project',
	},
	// window
	about: {
		label: 'About',
		callback: () => navigateTo('/about'),
		category: 'Window',
	},
	credits: {
		label: 'Credits',
		callback: () => navigateTo('/credits'),
		category: 'Window',
	},
	// assets: {
	// 	label: 'Assets', callback: () => navigateTo('/assets'), type: 'window', }, recordings: {
	// 	label: 'Recordings', callback: () => navigateTo('/recordings'), type: 'window', },
	templates: {
		label: 'Templates',
		callback: () => navigateTo('/templates'),
		category: 'Window',
	},
	feedback: {
		label: 'Send Feedback',
		callback: () => navigateTo('/feedback'),
		category: 'Window',
	},
	settings_general: {
		label: 'General',
		callback: () => navigateTo('/settings/general'),
		category: 'Settings',
	},
	settings_shortcuts: {
		label: 'Keyboard',
		callback: () => navigateTo('/settings/shortcuts'),
		category: 'Settings',
	},
	settings_midi: {
		label: 'MIDI',
		callback: () => navigateTo('/settings/midi'),
		category: 'Settings',
	},
	// view
	show_debug: {
		label: 'Debug',
		action: toggleDockPanel('debug'),
		category: 'View',
	},
	show_layers: {
		label: 'Layers',
		action: toggleDockPanel('layers'),
		category: 'View',
	},
	show_presets: {
		label: 'Presets',
		action: toggleDockPanel('presets'),
		category: 'View',
	},
	show_projects: {
		label: 'Projects',
		action: toggleDockPanel('projects'),
		category: 'View',
	},
	show_recordings: {
		label: 'Recordings',
		action: toggleDockPanel('recordings'),
		category: 'View',
	},
	show_images: {
		label: 'Images',
		action: toggleDockPanel('images'),
		category: 'View',
	},
	show_assets: {
		label: 'Assets',
		action: toggleDockPanel('assets'),
		category: 'View',
	},
	capture_image: {
		label: 'Capture Image',
		condition: () => {
			const s = store.getState()
			return selectIsConnected(s) && selectSessionStatus(s) === SESSION_STATUS.RUNNING
		},
		callback: () => captureImage(socket),
		category: 'App',
	},
	toggle_projection_map: {
		label: 'Map Editor',
		action: () => (dispatch, getState) => dispatch(selectProjectionMap(getState()) ? closeMap() : openActiveMap()),
		category: 'View',
	},
	// Controller and keyboard learn (context/input.md). Named apart from projection Mapping.
	learn: {
		label: 'Map MIDI',
		action: () => toggleLearn(),
		category: 'View',
	},
	show_output: {
		label: 'Output',
		action: toggleOutput,
		category: 'View',
	},
	open_output_tab: {
		label: 'Mirror window',
		callback: () => openMirror(),
		category: 'View',
	},
	show_source: {
		label: 'Stage',
		action: toggleUIProperty('show.source'),
		category: 'View',
	},
	show_minimal: {
		label: 'Minimal View',
		action: toggleUIProperty('show.minimal'),
		category: 'View',
	},
	reset_layout: {
		label: 'Reset Layout',
		callback: () => resetLayout(),
		category: 'View',
	},
	show_ui: {
		label: 'Hide User Interface',
		action: toggleUIProperty('show.ui'),
		category: 'View',
	},
	show_playback: {
		label: 'Playback Controls',
		action: toggleUIProperty('show.playback'),
		category: 'View',
	},
	show_param_column: {
		label: 'One param per row',
		action: toggleUIProperty('show.param_column'),
		category: 'View',
	},
	show_stats: {
		label: 'Stats',
		action: toggleShowStats(),
		category: 'View',
	},
	fullscreen: {
		label: 'Fullscreen',
		callback: () => {
			document.fullscreenElement ? document.exitFullscreen() : document.querySelector('body').requestFullscreen()
		},
		category: 'View',
	},
	// session
	start_session: {
		label: 'Start Session',
		action: () => startSession(),
		category: 'Session',
	},
	stop_session: {
		label: 'Stop Session',
		action: () =>
			requestConfirm({
				title: 'Stop Session',
				message: 'Are you sure you want to stop the session?',
				action: { type: 'stopSession' },
				confirmType: 'confirm_stop_session',
				destructive: true,
			}),
		category: 'Session',
	},
	// presets
	add_preset: {
		label: 'Add New',
		action: () => addPreset(),
		category: 'Presets',
	},
	save_preset: {
		label: 'Save',
		action: () => savePreset(),
		category: 'Presets',
	},
	set_as_project_thumb: {
		label: 'Set as project thumb',
		action: payload => setPresetAsProjectThumb(payload),
		category: 'Presets',
	},
	recapture_thumbnail: {
		label: 'Recapture Thumbnail',
		action: payload => recaptureThumbnail(payload),
		category: 'Presets',
	},
	examples_in_playlist: {
		label: 'Include in playlist',
		action: () => toggleExamplesInPlaylist(),
		category: 'Presets',
	},
	hide_examples: {
		label: 'Hide',
		action: () => hideExamples(),
		category: 'Presets',
	},
	// playback
	prev_preset: {
		label: 'Go To Prev',
		action: () => loadPrevPreset(),
		category: 'Presets',
	},
	next_preset: {
		label: 'Go To Next',
		action: () => loadNextPreset(),
		category: 'Presets',
	},
	next_preset_instant: {
		label: 'Go To Next (Instant)',
		action: () => loadNextPreset(true),
		category: 'Presets',
	},
	prev_preset_instant: {
		label: 'Go To Prev (Instant)',
		action: () => loadPrevPreset(true),
		category: 'Presets',
	},
	toggle_playback: {
		label: 'Play',
		action: () => togglePlayback(),
		category: 'Autoplay',
	},
	// video
	/**
	 * Space. The transport for whatever is playing: the video file when one is loaded, autoplay when none is — a video is the rare case, so the bare key belongs to the playlist the rest of the time.
	 *
	 * ⚠️ ONE action owns the key rather than two sharing it. `updateBinding` deletes any other action holding a hotkey and `validateBindings` reports duplicates as conflicts, so a second binding on `Space` would surface as a user-facing conflict and be clobbered on the next rebind. The branch lives here instead, where rebinding still moves both halves together.
	 *
	 * ⚠️ It REPLACES `toggle_video` rather than joining it. Persisted bindings outrank the defaults and the whole set is stored verbatim on first run, so an existing user's stored `toggle_video: 'Space'` would outlive the change and collide — retiring the action name is what lets the reconciler drop it (`~/store`).
	 *
	 * `toggle_playback` (Ctrl+Space) stays autoplay unconditionally, so the playlist is always reachable while a video is loaded.
	 */
	tap_tempo: {
		label: 'Tap tempo',
		callback: () => tempo.tap(),
		category: 'Clock',
	},
	downbeat: {
		label: 'Downbeat',
		callback: () => tempo.downbeat(),
		category: 'Clock',
	},
	tempo_double: {
		label: 'Double tempo',
		condition: () => tempo.snapshot().bpm * 2 <= MAX_BPM,
		callback: () => tempo.setBpm(tempo.snapshot().bpm * 2),
		category: 'Clock',
	},
	tempo_halve: {
		label: 'Halve tempo',
		condition: () => tempo.snapshot().bpm / 2 >= MIN_BPM,
		callback: () => tempo.setBpm(tempo.snapshot().bpm / 2),
		category: 'Clock',
	},
	tempo_run: {
		label: 'Run/Pause clock',
		callback: () => (tempo.snapshot().playing ? tempo.pause() : tempo.play()),
		category: 'Clock',
	},
	toggle_transport: {
		label: 'Play/Pause',
		callback: () => {
			if (hasVideoFile()) playhead.togglePlay()
			else store.dispatch(togglePlayback())
		},
		category: 'Video',
	},
	video_seek_back: {
		label: 'Seek Back 1s',
		condition: hasVideoFile,
		callback: () => playhead.seekBy(-1),
		category: 'Video',
	},
	video_seek_forward: {
		label: 'Seek Forward 1s',
		condition: hasVideoFile,
		callback: () => playhead.seekBy(1),
		category: 'Video',
	},
	video_seek_back_5: {
		label: 'Seek Back 5s',
		condition: hasVideoFile,
		callback: () => playhead.seekBy(-5),
		category: 'Video',
	},
	video_seek_forward_5: {
		label: 'Seek Forward 5s',
		condition: hasVideoFile,
		callback: () => playhead.seekBy(5),
		category: 'Video',
	},
	video_frame_prev: {
		label: 'Previous Frame',
		condition: () => hasVideoFile() && !!window.video_file_element._fps,
		callback: () => playhead.step(-1),
		category: 'Video',
	},
	video_frame_next: {
		label: 'Next Frame',
		condition: () => hasVideoFile() && !!window.video_file_element._fps,
		callback: () => playhead.step(1),
		category: 'Video',
	},
	video_mark_in: {
		label: 'Loop In at Playhead',
		condition: hasVideoFile,
		callback: () => playhead.markAt('in'),
		category: 'Video',
	},
	video_mark_out: {
		label: 'Loop Out at Playhead',
		condition: hasVideoFile,
		callback: () => playhead.markAt('out'),
		category: 'Video',
	},
	// editor
	undo: {
		label: 'Undo',
		action: 'history/undo',
		category: 'Edit',
	},
	redo: {
		label: 'Redo',
		action: 'history/redo',
		category: 'Edit',
	},
	copy: {
		label: 'Copy',
		action: () => copyAction(),
		category: 'Edit',
	},
	cut: {
		label: 'Cut',
		action: () => cutAction(),
		category: 'Edit',
	},
	paste: {
		label: 'Paste',
		action: () => pasteAction(),
		category: 'Edit',
	},
	delete: {
		label: 'Delete',
		action: () => deleteAction(),
		category: 'Edit',
	},
	rename: {
		label: 'Rename',
		action: startRename,
		category: 'Edit',
	},
	// tools
	move_tool: {
		label: 'Move',
		action: setCurrentTool('move'),
		category: 'Tools',
	},
	edit_path_tool: {
		label: 'Edit Path',
		action: setCurrentTool('edit_path'),
		category: 'Tools',
	},
	transform_tool: {
		label: 'Transform',
		action: setCurrentTool('transform'),
		category: 'Tools',
	},
	// add
	add_circle: {
		label: 'Add Circle',
		action: setCurrentTool('add_circle'),
		category: 'Tools',
	},
	add_rect: {
		label: 'Add Rectangle',
		action: setCurrentTool('add_rect'),
		category: 'Tools',
	},
	add_path: {
		label: 'Add Path',
		action: setCurrentTool('add_path'),
		category: 'Tools',
	},
	add_image: {
		label: 'Add Image',
		action: () => expandAndFlashPanel('assets', '[data-panel="assets"] [data-context="asset"]'),
		category: 'Tools',
	},
	add_brush: {
		label: 'Brush',
		action: setCurrentTool('add_brush'),
		category: 'Tools',
	},
	eraser_tool: {
		label: 'Eraser',
		action: setCurrentTool('eraser'),
		category: 'Tools',
	},
	// objects
	group_objects: {
		label: 'Group',
		action: () => groupSelectedObjectsThunk(),
		category: 'Object',
	},
	ungroup_objects: {
		label: 'Ungroup',
		action: () => (dispatch, getState) => {
			const state = getState()
			const ids = state.selection.objectIds
			if (!ids?.length) return
			const objects = state.live.objects
			const hasGroups = ids.some(id => objects[id]?.type === 'group')
			if (hasGroups) {
				dispatch(unGroupSelectedObjectsThunk())
				return
			}
			const textObjs = ids.map(id => objects[id]).filter(o => o?.type === 'text' && o.text?.trim())
			if (textObjs.length) {
				if (textObjs.some(o => o.text.length > 1)) {
					dispatch(breakApartTextThunk())
				} else {
					dispatch(expandTextThunk())
				}
			}
		},
		category: 'Object',
	},
	bringToFront: {
		label: 'Bring to Front',
		action: () => bringSelectedObjectsToFrontThunk(),
		category: 'Object',
	},
	sendToBack: {
		label: 'Send to Back',
		action: () => sendSelectedObjectsToBackThunk(),
		category: 'Object',
	},
	flip_horizontal: {
		label: 'Flip Horizontal',
		action: () => flipSelectedObjectsThunk('x'),
		category: 'Object',
	},
	flip_vertical: {
		label: 'Flip Vertical',
		action: () => flipSelectedObjectsThunk('y'),
		category: 'Object',
	},
	reset_object: {
		label: 'Reset',
		action: () => resetObjectsThunk(),
		category: 'Object',
	},
	lock_objects: {
		label: 'Lock',
		action: () => lockSelectedObjectsThunk(),
		category: 'Object',
	},
	unlock_objects: {
		label: 'Unlock',
		action: () => unlockSelectedObjectsThunk(),
		category: 'Object',
	},
	break_apart_text: {
		label: 'Break Apart',
		action: () => breakApartTextThunk(),
		category: 'Object',
	},
	expand_text: {
		label: 'Expand to Path',
		action: () => expandTextThunk(),
		category: 'Object',
	},
	unite_objects: {
		label: 'Unite',
		action: () => uniteSelectedObjectsThunk(),
		category: 'Object',
	},
	subtract_objects: {
		label: 'Subtract',
		action: payload => subtractSelectedObjectsThunk(payload),
		category: 'Object',
	},
	// misc
	open_guides: {
		label: 'Guides',
		// DRAFT: in dev, open this app's own /guides page so the docs can be reviewed. Prod keeps pointing at the marketing site — which is owned by another team and has no /guides route yet, so that link still 404s. This gates the LINK, not the route: /guides stays reachable by URL anywhere.
		callback: () => window.open('/guides', '_blank'),
		category: 'App',
	},
	download_daemon: {
		label: 'Get Resolume Plugin',
		callback: () => window.open(DOWNLOAD_DAEMON_URL, '_blank'),
		category: 'App',
	},
	log_out: {
		label: 'Logout',
		callback: () => logout(),
		category: 'App',
	},
	share_recording: {
		label: 'Share',
		action: () => shareRecording(),
		category: 'Recordings',
	},
	download_recording: {
		label: 'Download',
		action: () => downloadRecording(),
		category: 'Recordings',
	},
	download_recording_source: {
		label: 'Download Source',
		action: () => downloadRecordingSource(),
		category: 'Recordings',
	},
	download_asset: {
		label: 'Download',
		action: () => downloadAsset(),
		category: 'Assets',
	},
	download_image: {
		label: 'Download',
		action: () => downloadImage(),
		category: 'Assets',
	},
	set_as_image_style: {
		label: 'Use as Image Style',
		action: () => setAsImageStyle(),
		category: 'Assets',
	},
	add_source: {
		label: 'Add as New Layer',
		action: p => pickSourceItem({ id: p?.id, mode: 'add' }),
		category: 'Sources',
	},
	replace_source: {
		label: 'Replace',
		action: p => pickSourceItem({ id: p?.id, mode: 'replace' }),
		category: 'Sources',
	},
	detach_map: {
		label: 'Detach from Map',
		action: () => detachFromMap(selectSelectedObjectIds(store.getState())),
		category: 'Sources',
	},
	open_recording: {
		label: 'Open',
		action: () => openRecording(),
		category: 'Recordings',
	},
	clear_app_cache: {
		label: 'Clear Cache & Reload',
		callback: () => clearAppCache(),
		category: 'App',
	},
	// admin
	open_examples_mobile: {
		label: 'Edit Mobile Examples',
		action: () => openExamplesProject('mobile'),
		category: 'Admin',
	},
	open_examples_desktop: {
		label: 'Edit Desktop Examples',
		action: () => openExamplesProject('desktop'),
		category: 'Admin',
	},
	// focus
	focus_prompt: {
		label: 'Focus on Text Prompt',
		callback: () => {
			const promptInput = document.querySelector('textarea[name="diffusion.prompt"]')
			if (promptInput) {
				promptInput.focus()
			}
		},
		category: 'Focus',
	},
	focus_preset_name_input: {
		label: 'Focus on preset input',
		callback: () => {
			document.getElementById('preset_name_input').focus()
		},
		category: 'Focus',
	},
	// key kept as copy_session_arn — it's referenced by keybindings (Ctrl+Shift+C)
	copy_session_arn: {
		label: 'Copy Session Info',
		condition: () => !!(IS_LOCAL || IS_DEV || IS_DEBUG),
		callback: () => {
			// AWS sessions: the task ARN. GCP sessions: the log-locator fields (see context/tools/gcp-logs.md). Cloud detected from extraData shape.
			const session = window.session
			const extra = session?.extraData
			if (!extra) return
			const text = extra.taskArn
				? extra.taskArn
				: JSON.stringify({
						sessionId: session.id,
						instanceName: extra.instanceName,
						instanceId: extra.instanceId,
						zone: extra.zone,
						region: extra.region,
					})
			navigator.clipboard.writeText(text)
		},
		category: 'App',
	},
}

// Add name property to each action
for (const a in actions) {
	actions[a].name = a
}

Object.freeze(actions)

export default actions
